import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { ActGate } from '@/components/composed/ActGate';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/lib/session/auth-store';
import type { Invoice, InvoiceListParams } from '@/lib/api/domains/invoices';
import { invoiceListQuery } from '../api/invoice-queries';
import { invoiceColumns } from './invoice-columns';

/*
 * Feature component (spec tier rule): binds the query + columns to the
 * domain-free DataTable. Table page/sort state lives in the URL and is passed
 * in as `params`; navigation is delegated to the route via callbacks. The
 * per-row edit action is permission-gated here (server is still the authority).
 */
export function InvoiceTable({
  params,
  onPageChange,
  onRowClick,
  onEdit,
}: {
  params: InvoiceListParams;
  onPageChange: (page: number) => void;
  onRowClick: (invoice: Invoice) => void;
  onEdit?: (invoice: Invoice) => void;
}) {
  const query = useQuery(invoiceListQuery(params));
  const session = useAuthStore((s) => s.session);

  return (
    <DataTable<Invoice>
      columns={invoiceColumns}
      data={query.data?.items ?? []}
      getRowId={(row) => row.id}
      tableId="invoices"
      actions={
        onEdit
          ? (invoice) => (
              /*
               * The per-row edit is wrapped in ActGate at this real surface
               * (E01.F03b.R60, AC-6c3226b8): the control is OFFERED only when a
               * permission set the caller holds covers the act, read per-act
               * from the server-sourced capabilities (`canOfferAct`). This
               * proves the at-every-surface withholding through a REAL
               * composition, not a stand-in. ActGate renders nothing for a
               * caller without the act, so a non-permitted caller sees no edit
               * control; the server is the backstop regardless.
               */
              <ActGate session={session} act="invoices:write">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit invoice ${invoice.number}`}
                  onClick={() => onEdit?.(invoice)}
                >
                  <Pencil className="size-4" />
                </Button>
              </ActGate>
            )
          : undefined
      }
      isLoading={query.isLoading}
      isError={query.isError}
      errorMessage="Failed to load invoices."
      emptyTitle="No invoices found"
      emptyDescription="Try adjusting your filters, or create a new invoice."
      page={query.data?.page ?? params.page ?? 1}
      pageSize={query.data?.pageSize ?? params.pageSize ?? 25}
      total={query.data?.total ?? 0}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
    />
  );
}
